import React, { useState, useMemo } from 'react';
import { 
  Plus, X, Search, AlertTriangle, Heart, Pill, Stethoscope, 
  ChevronDown, ChevronRight, Info, Check, AlertCircle 
} from 'lucide-react';
import { HEALTH_CATEGORIES } from '../data/healthData';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../i18n';

const HealthManager = ({ 
  value = { allergies: [], conditions: [], medications: [] },
  onChange,
  isDarkMode = false,
  className = ""
}) => {
  const { language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('allergies');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSubcategories, setExpandedSubcategories] = useState({});

  // Category tabs
  const categories = [
    { 
      id: 'allergies', 
      label: t('health.allergies', 'Alerjiler'), 
      icon: AlertTriangle, 
      color: 'red',
      data: HEALTH_CATEGORIES.ALLERGIES
    },
    { 
      id: 'conditions', 
      label: t('health.conditions', 'Sağlık Durumları'), 
      icon: Heart, 
      color: 'blue',
      data: HEALTH_CATEGORIES.CONDITIONS
    },
    { 
      id: 'medications', 
      label: t('health.medications', 'İlaçlar'), 
      icon: Pill, 
      color: 'green',
      data: HEALTH_CATEGORIES.MEDICATIONS
    }
  ];

  // Get current category data
  const currentCategory = categories.find(cat => cat.id === activeCategory);
  
  // Search functionality
  const searchResults = useMemo(() => {
    if (!searchTerm.trim() || !currentCategory) return null;
    
    const results = [];
    const term = searchTerm.toLowerCase();
    
    Object.entries(currentCategory.data.subcategories).forEach(([subKey, subcategory]) => {
      subcategory.items.forEach(item => {
        const itemName = language === 'en' ? item.nameEn : item.name;
        if (itemName.toLowerCase().includes(term)) {
          results.push({
            ...item,
            subcategory: subcategory.name,
            subcategoryKey: subKey
          });
        }
      });
    });
    
    return results;
  }, [searchTerm, currentCategory, language]);

  // Toggle subcategory expansion
  const toggleSubcategory = (subKey) => {
    setExpandedSubcategories(prev => ({
      ...prev,
      [subKey]: !prev[subKey]
    }));
  };

  // Add health item
  const addHealthItem = (item, category) => {
    const newValue = {
      ...value,
      [category]: [...(value[category] || []), {
        ...item,
        id: Date.now(),
        addedAt: new Date().toISOString()
      }]
    };
    onChange(newValue);
  };

  // Remove health item
  const removeHealthItem = (itemId, category) => {
    const newValue = {
      ...value,
      [category]: value[category].filter(item => item.id !== itemId)
    };
    onChange(newValue);
  };

  // Add custom item
  const addCustomItem = (customText, category) => {
    if (!customText.trim()) return;
    
    const customItem = {
      name: customText.trim(),
      nameEn: customText.trim(),
      severity: 'medium',
      isCustom: true,
      id: Date.now(),
      addedAt: new Date().toISOString()
    };
    
    addHealthItem(customItem, category);
  };

  // Check if item is selected
  const isItemSelected = (item) => {
    const categoryItems = value[activeCategory] || [];
    return categoryItems.some(selected => 
      selected.name === item.name || selected.nameEn === item.nameEn
    );
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return isDarkMode ? 'text-red-400' : 'text-red-600';
      case 'medium': return isDarkMode ? 'text-yellow-400' : 'text-yellow-600';
      case 'low': return isDarkMode ? 'text-green-400' : 'text-green-600';
      default: return isDarkMode ? 'text-gray-400' : 'text-gray-600';
    }
  };

  // Get severity badge
  const getSeverityBadge = (severity) => {
    const colors = {
      high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    };
    
    const labels = {
      high: t('health.severityHigh', 'Yüksek'),
      medium: t('health.severityMedium', 'Orta'),
      low: t('health.severityLow', 'Düşük')
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[severity]}`}>
        {labels[severity]}
      </span>
    );
  };

  return (
    <div className={`health-manager ${className}`}>
      {/* Category Tabs */}
      <div className={`flex space-x-1 p-1 rounded-lg mb-6 ${
        isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
      }`}>
        {categories.map(category => {
          const Icon = category.icon;
          const isActive = activeCategory === category.id;
          
          return (
            <button
              key={category.id}
              onClick={() => {
                setActiveCategory(category.id);
                setSearchTerm('');
                setExpandedSubcategories({});
              }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                isActive
                  ? `bg-${category.color}-500 text-white shadow-md`
                  : isDarkMode
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
              }`}
            >
              <Icon size={16} />
              <span>{category.label}</span>
              {value[category.id]?.length > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : isDarkMode
                      ? 'bg-gray-600 text-gray-200'
                      : 'bg-gray-200 text-gray-700'
                }`}>
                  {value[category.id].length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search 
            size={16} 
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} 
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('health.searchPlaceholder', 'Sağlık durumu ara...')}
            className={`w-full pl-10 pr-4 py-3 rounded-xl border ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available Items */}
        <div className={`rounded-xl border ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`font-semibold flex items-center ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <Plus size={16} className="mr-2" />
              {t('health.availableItems', 'Mevcut Seçenekler')}
            </h3>
          </div>

          <div className="p-4 max-h-80 overflow-y-auto">
            {searchResults ? (
              /* Search Results */
              <div>
                {searchResults.length > 0 ? (
                  <div className="space-y-2">
                    {searchResults.map((item, index) => (
                      <HealthItem
                        key={index}
                        item={item}
                        isSelected={isItemSelected(item)}
                        onToggle={() => {
                          if (isItemSelected(item)) {
                            const selectedItem = value[activeCategory].find(selected => 
                              selected.name === item.name || selected.nameEn === item.nameEn
                            );
                            removeHealthItem(selectedItem.id, activeCategory);
                          } else {
                            addHealthItem(item, activeCategory);
                          }
                        }}
                        isDarkMode={isDarkMode}
                        language={language}
                        getSeverityBadge={getSeverityBadge}
                        showSubcategory={true}
                      />
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-8 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <Search size={24} className="mx-auto mb-2 opacity-50" />
                    <p>{t('health.noSearchResults', 'Arama sonucu bulunamadı')}</p>
                  </div>
                )}
              </div>
            ) : (
              /* Category Structure */
              <div className="space-y-4">
                {Object.entries(currentCategory.data.subcategories).map(([subKey, subcategory]) => (
                  <div key={subKey}>
                    {/* Subcategory Header */}
                    <button
                      onClick={() => toggleSubcategory(subKey)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                        isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <span className={`font-medium ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {language === 'en' ? subcategory.nameEn : subcategory.name}
                      </span>
                      <ChevronRight 
                        size={16} 
                        className={`transform transition-transform ${
                          expandedSubcategories[subKey] ? 'rotate-90' : ''
                        } ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                      />
                    </button>

                    {/* Subcategory Items */}
                    {expandedSubcategories[subKey] && (
                      <div className="mt-2 ml-3 space-y-1">
                        {subcategory.items.map((item, index) => (
                          <HealthItem
                            key={index}
                            item={item}
                            isSelected={isItemSelected(item)}
                            onToggle={() => {
                              if (isItemSelected(item)) {
                                const selectedItem = value[activeCategory].find(selected => 
                                  selected.name === item.name || selected.nameEn === item.nameEn
                                );
                                removeHealthItem(selectedItem.id, activeCategory);
                              } else {
                                addHealthItem(item, activeCategory);
                              }
                            }}
                            isDarkMode={isDarkMode}
                            language={language}
                            getSeverityBadge={getSeverityBadge}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Custom Item Input */}
            <CustomItemInput
              onAdd={(text) => addCustomItem(text, activeCategory)}
              isDarkMode={isDarkMode}
              placeholder={t('health.addCustom', 'Özel sağlık durumu ekle...')}
            />
          </div>
        </div>

        {/* Selected Items */}
        <div className={`rounded-xl border ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`font-semibold flex items-center ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <Check size={16} className="mr-2" />
              {t('health.selectedItems', 'Seçili Durumlar')}
              {value[activeCategory]?.length > 0 && (
                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                  isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                }`}>
                  {value[activeCategory].length}
                </span>
              )}
            </h3>
          </div>

          <div className="p-4 max-h-80 overflow-y-auto">
            {value[activeCategory]?.length > 0 ? (
              <div className="space-y-2">
                {value[activeCategory].map((item) => (
                  <SelectedHealthItem
                    key={item.id}
                    item={item}
                    onRemove={() => removeHealthItem(item.id, activeCategory)}
                    isDarkMode={isDarkMode}
                    language={language}
                    getSeverityBadge={getSeverityBadge}
                  />
                ))}
              </div>
            ) : (
              <div className={`text-center py-8 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <AlertCircle size={24} className="mx-auto mb-2 opacity-50" />
                <p>{t('health.noSelectedItems', 'Henüz seçili durum yok')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Health Item Component
const HealthItem = ({ 
  item, 
  isSelected, 
  onToggle, 
  isDarkMode, 
  language, 
  getSeverityBadge,
  showSubcategory = false 
}) => {
  const itemName = language === 'en' ? item.nameEn : item.name;
  
  return (
    <div
      onClick={onToggle}
      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
        isSelected
          ? isDarkMode
            ? 'border-blue-500 bg-blue-500/20'
            : 'border-blue-500 bg-blue-50'
          : isDarkMode
            ? 'border-gray-700 hover:border-gray-600 bg-gray-800'
            : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className={`font-medium ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {itemName}
          </div>
          {showSubcategory && item.subcategory && (
            <div className={`text-sm mt-1 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {item.subcategory}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {getSeverityBadge(item.severity)}
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
            isSelected
              ? 'border-blue-500 bg-blue-500'
              : isDarkMode
                ? 'border-gray-600'
                : 'border-gray-300'
          }`}>
            {isSelected && <Check size={12} className="text-white" />}
          </div>
        </div>
      </div>
    </div>
  );
};

// Selected Health Item Component
const SelectedHealthItem = ({ 
  item, 
  onRemove, 
  isDarkMode, 
  language, 
  getSeverityBadge 
}) => {
  const itemName = language === 'en' ? item.nameEn : item.name;
  
  return (
    <div className={`p-3 rounded-lg border ${
      isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className={`font-medium ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {itemName}
            {item.isCustom && (
              <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
              }`}>
                {t('health.custom', 'Özel')}
              </span>
            )}
          </div>
          {item.addedAt && (
            <div className={`text-xs mt-1 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {t('health.addedOn', 'Eklenme:')} {new Date(item.addedAt).toLocaleDateString()}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {getSeverityBadge(item.severity)}
          <button
            onClick={onRemove}
            className={`p-1 rounded-full transition-colors ${
              isDarkMode
                ? 'hover:bg-red-500/20 text-red-400 hover:text-red-300'
                : 'hover:bg-red-50 text-red-500 hover:text-red-600'
            }`}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Custom Item Input Component
const CustomItemInput = ({ onAdd, isDarkMode, placeholder }) => {
  const [customText, setCustomText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (customText.trim()) {
      onAdd(customText);
      setCustomText('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <div className="flex space-x-2">
        <input
          type="text"
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          placeholder={placeholder}
          className={`flex-1 px-3 py-2 rounded-lg border ${
            isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
        />
        <button
          type="submit"
          disabled={!customText.trim()}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            customText.trim()
              ? isDarkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
              : isDarkMode
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <Plus size={16} />
        </button>
      </div>
    </form>
  );
};

export default HealthManager;
